import type { HarmonyPackageBoard, HarmonyPackageBoardWorkitem, HarmonyWorkitem, Workitem } from '@rhythmic/db/types'
import type { KanbanCard } from '@rhythmic/types'
import type { Collection, UseLiveQueryReturn } from '@tanstack/vue-db'
import { eq, useLiveQuery } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import {
  getBoardItemsCollection,
  getBoardsCollection,
  getHarmonyItemsCollection,
  getWorkitemsCollection,
} from '../contexts/getCollections'

const logger = useRhythmicLogger('useBoardWorkitemsByBoardId')

export function useBoardWorkitemsByBoardId(
  boardId: string,
  workspaceId: string,
  planId: string,
  teamId: string,
): UseLiveQueryReturn<KanbanCard> | undefined {
  logger.debug('useBoardWorkitemsByBoardId called', {
    boardId,
    workspaceId,
    planId,
    teamId,
  })
  if (!boardId || !workspaceId || !planId || !teamId) {
    logger.warn('useBoardWorkitemsByBoardId: Missing required parameters', {
      boardId,
      workspaceId,
      planId,
      teamId,
    })
    return undefined
  }

  const boardsCollection: Collection<HarmonyPackageBoard> = getBoardsCollection(planId)
  const boardItemsCollection: Collection<HarmonyPackageBoardWorkitem> = getBoardItemsCollection(boardId)
  const harmonyItemsCollection: Collection<HarmonyWorkitem> = getHarmonyItemsCollection(planId)
  const workitemsCollection: Collection<Workitem> = getWorkitemsCollection(teamId)

  logger.debug('Collections acquired', {
    boardsCollection,
    boardItemsCollection,
    harmonyItemsCollection,
    workitemsCollection,
  })

  const query = useLiveQuery((builder) =>
    builder
      .from({ boardItem: boardItemsCollection })
      .innerJoin({ harmonyWorkitem: harmonyItemsCollection }, ({ boardItem, harmonyWorkitem }) =>
        eq(boardItem.workitem_id, harmonyWorkitem.id),
      )
      .innerJoin({ workitem: workitemsCollection }, ({ harmonyWorkitem, workitem }) =>
        eq(harmonyWorkitem.workitem_id, workitem.id),
      )
      .select(({ boardItem, workitem }) => ({
        id: boardItem.id,
        position: boardItem.position,
        columnId: boardItem.column_id,
        harmonyWorkitemId: boardItem.workitem_id,
        item: workitem,
        boardId: boardItem.board_id,
      })),
  )
  logger.debug('Query constructed', { query })
  return query as UseLiveQueryReturn<KanbanCard>
}
